import React from 'react'
import PropTypes from 'prop-types'
import { Field, FieldArray, getIn } from 'formik'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Input, Error, RichText, File } from '@peracto/peracto-ui'
import sortBy from 'lodash/sortBy'
import isString from 'lodash/isString'
import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash'
import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons/faPlusCircle'

const renderField = (input, fieldName) => {
    switch (input.type) {
        case 'richtext':
            return <RichText name={fieldName} hideCharacterCount />
        case 'ckfinder':
            return <File name={fieldName} />
        default:
            return <Input name={fieldName} type={input.type} {...input?.options} />
    }
}

const InputRows = ({ label, description, name, fields }) => {
    const inputs = []
    const defaultRow = {}

    for (const [key, val] of Object.entries(fields)) {
        inputs.push({
            name: key,
            ...val,
        })

        defaultRow[key] = ''
    }

    const sortedInputs = sortBy(inputs, ['display_order'])

    return (
        <Field name={name}>
            {({ form, field }) => (
                <FieldArray name={name}>
                    {({ push, remove }) => {
                        const values = field?.value

                        let error = getIn(form.errors, name)
                        if (error && isString(error)) {
                            const errorFieldName = label ? label : 'This'
                            error = error.replace(field.name, errorFieldName)
                        }

                        return (
                            <div className="!flex !flex-col">
                                {label && (
                                    <label className={description ? '!mb-0' : ''}>{label}</label>
                                )}

                                {description && (
                                    <p className={`help-text text-grey ${error ? '!mb-0' : ''}`}>
                                        {description}
                                    </p>
                                )}

                                {error && isString(error) && (
                                    <div className="!mb-2">
                                        <Error error={error} name={name} />
                                    </div>
                                )}

                                <table className="table table-sm" style={{ tableLayout: 'fixed' }}>
                                    <thead>
                                        <tr>
                                            {sortedInputs?.map(input => (
                                                <th
                                                    key={`${input.name}_th`}
                                                    style={{
                                                        width: `${100 / sortedInputs.length}%`,
                                                    }}
                                                >
                                                    {input.label}
                                                </th>
                                            ))}
                                            <th className="!text-right" style={{ width: '70px' }}>
                                                Remove
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {values?.length > 0 &&
                                            values.map((item, index) => (
                                                <tr
                                                    key={`${name}_${index}`}
                                                    data-id={`${name}_${index}`}
                                                >
                                                    {sortedInputs?.map(input => (
                                                        <td key={`${input.name}_${index}`}>
                                                            {renderField(
                                                                input,
                                                                `${name}.${index}.${input.name}`
                                                            )}
                                                        </td>
                                                    ))}
                                                    <td className="!text-right">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-danger"
                                                            onClick={() => {
                                                                remove(index, 1)
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                                <button
                                    type="button"
                                    role="button"
                                    className="btn btn-primary !mb-2 !self-end"
                                    onClick={() => push(defaultRow)}
                                >
                                    <FontAwesomeIcon icon={faPlusCircle} className="!mr-2" />
                                    Add a value
                                </button>
                            </div>
                        )
                    }}
                </FieldArray>
            )}
        </Field>
    )
}

InputRows.displayName = 'InputRows'

InputRows.propTypes = {
    label: PropTypes.string,
    description: PropTypes.string,
    name: PropTypes.string.isRequired,
    fields: PropTypes.object.isRequired,
}

export default InputRows
